<template>
  <div class="relative">
    <template v-if="userHasAccess">
      <div
        v-if="!allDataLoaded && !loadError"
        class="loading-overlay">
        <div class="">
          <div class="loading-spinner pb-3 pt-3">
            <span class="loading-spinner-content">
              {{ $t('ui.loadingDataInProgress') }}&hellip;
            </span>
          </div>
        </div>
      </div>
      <template v-if="loadError">
        <div class="wrapper mt-3">
          <f-alert
            visible
            theme="warning">
            <span>
              {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
          </span>
          </f-alert>
        </div>
      </template>
      <template v-else>
        <div class="wrapper">
          <div class="card flex-row is-smallshadow mt-2 py-2 px-2 width-medium mx-auto flex-wrap">
            <f-button
              class="text-uppercase pr-1-sm"
              theme="go-back"
              withArrow
              withArrowBackwards
              @click.prevent="$router.push('/artykuly')">
              {{ $t('articleForm.backToArticleList') }}
            </f-button>

            <f-badge
              v-if="currentStatus"
              class="ml-auto"
              :theme="currentStatus.badgeTheme">
              {{ currentStatus.text }}
            </f-badge>

          </div>
        </div>
        <div class="width-medium mx-auto">
          <div class="card is-smallshadow mt-2 mb-2">
            <div class="card-body px-3 pt-3">
              <h2 class="heading-small mb-2">{{ $t('articleForm.article') }}</h2>
              <validation-observer
                ref="article-form"
                tag="form">

                <div
                  v-if="editMode"
                  class="form-group pb-2 mb-2 border-bottom">
                  <label for="title">{{ $t('articleForm.adminComment') }}</label>
                    {{ data.comment || this.$t('ui.none').toLowerCase() }}
                </div>

                <div class="form-group pb-2 mb-2 border-bottom">
                  <label for="title">{{ $t('ui.title') }}</label>

                  <template v-if="data.can_update">
                    <validation-provider
                      rules="required"
                      name="title"
                      v-slot="{ errors }">
                      <f-input
                        v-model="data.title"
                        :state="errors.length ? 'invalid' : null"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </template>
                  <template v-else>
                    {{ data.title }}
                  </template>
                </div>

                <div class="form-group pb-2 mb-2 border-bottom">
                  <label for="content">{{ $t('ui.content') }}</label>
                  <template v-if="data.can_update">
                    <validation-provider
                      rules="required"
                      name="content"
                      v-slot="{ errors }">
                      <textarea
                        id="wysiwyg"
                        v-model="data.content">
                      </textarea>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </template>
                  <template v-else>
                    <div v-pure-html="data.content"></div>
                  </template>
                </div>
              </validation-observer>
            </div>
          </div>

          <div class="flex">
            <f-button
              :disabled="!data.can_update"
              theme="primary"
              class="ml-auto"
              big
              withArrow
              @click.prevent="validateForm">
              {{ $t('ui.save') }}
            </f-button>
          </div>
        </div>
      </template>
    </template>
    <div
      v-else
      class="wrapper pt-3 pb-1">
      <f-alert
        visible
        class="my-2"
        theme="inactive">
        <span>
        {{ $t('user.currentPackageDoesNotIncludeAccessToSection') }}
        </span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import ArticleStatuses from '@/data/article-statuses.js';
import editorLangPl from '@/lang/tinymce-pl.json';

export default {
  name: 'article-form',
  computed: {
    allDataLoaded () {
      return this.loaded && (this.data.can_edit === true ? this.editorLoaded : true);
    },
    editMode () {
      return !!this.$route.params.id;
    },
    articleStatuses () {
      return ArticleStatuses;
    },
    currentStatus () {
      return this.articleStatuses.find(item => item.value === this.data.status);
    },
    userHasAccess () {
      return this.$store.getters.getUserHasFeature('articles');
    },
  },
  watch: {
    userHasAccess (newValue) {
      if (newValue && !this.viewInitiated) {
        this.initView();
      }
    }
  },
  data () {
    return {
      data: {
        can_update: null,
        comment: '',
        content: '',
        id: 0,
        status: '',
        title: ''
      },
      editorLoaded: false,
      loaded: false,
      loadError: false,
      viewInitiated: false
    };
  },
  mounted () {
    if (this.userHasAccess && !this.viewInitiated) {
      this.initView();
    }
  },
  methods: {
    initView () {
      if (!this.editMode) {
        Vue.set(this, 'loaded', true);
        Vue.set(this.data, 'can_update', true);
        Vue.nextTick(() => {
          this.initTinyMceScript();
        });
      } else {
        this.loadItemData();
      }
    },
    initTinyMceScript () {
      if (!document.body.querySelector('#tinymce-script-instance')) {
        let script = document.createElement('script');
        if (this.$http.defaults.baseURL.includes('localhost')) {
          script.setAttribute('src', '/vendor/tinymce/js/tinymce/tinymce.min.js');
        } else {
          script.setAttribute('src', '/panel-assets/vendor/tinymce/js/tinymce/tinymce.min.js');
        }
        script.setAttribute('id', 'tinymce-script-instance');
        script.addEventListener('load', () => {
          this.initTinyMce();
        });
        document.body.appendChild(script);
      } else {
        this.initTinyMce();
      }
    },
    initTinyMce () {
      Vue.set(this, 'editorLoaded', true);
      // eslint-disable-next-line
      tinymce.init({
        selector: '#wysiwyg',
        plugins: 'lists',
        menubar: false,
        toolbar1: 'styles bold italic underline strikethrough backcolor alignleft aligncenter alignright alignjustify bullist numlist outdent indent removeformat help',
        toolbar2: 'undo redo',
        height: '560',
        language: 'pl'
      });

      if (this.$i18n.locale === 'pl') {
        // eslint-disable-next-line
        tinymce.addI18n('pl', editorLangPl);
      }

      Vue.nextTick(() => {
        // eslint-disable-next-line
        tinymce.activeEditor.setContent(this.data.content);
      })
    },
    loadItemData () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/adviser/adviser/articles/item/',
        instanceFields: [
          'id',
          'title',
          'content',
          'can_update',
          'comment',
          'status'
        ],
        booleanFields: ['can_update'],
        loadedKey: 'loaded',
        successAction: () => {
          Vue.nextTick(() => {
            if (this.data.can_update) {
              this.initTinyMceScript();
            }
          })
        }
      });
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.editMode ? '/api/adviser/adviser/articles/update/' + this.data.id : '/api/adviser/adviser/articles/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('articleForm.saveArticle'),
        successText: this.$t('articleForm.articleSaveAndSend'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('articleForm.saveArticleError'),
        successAction: () => {
          this.$router.push('/artykuly');
        }
      });
    },
    validateForm () {
      this.beforeSaveData();
      Vue.nextTick(() => {
        FormUtils.validate(this.$refs['article-form'], this.submitForm);
      })
    },
    beforeSaveData () {
      // eslint-disable-next-line
      Vue.set(this.data, 'content', tinymce.activeEditor.getContent());
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        title: this.data.title,
        content: this.data.content
      };

      return formDataToSend;
    }
  },
  beforeDestroy () {
    if (this.editorLoaded) {
      // eslint-disable-next-line
      tinymce.remove();
    }
  }
};
</script>
